import { useMemo } from "react";

import { isLoggedIn, LogSentryException, ServiceError } from "@shapr3d/frontend-lib";
import {
  InfiniteData,
  QueryKey,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";
import { z } from "zod";

import { SyncRequestClient } from "../stores/requestClient";
import { useVisitedViewLinks } from "../stores/visitedViewLinksStore";
import {
  ProjectID,
  ProjectRoleUtils,
  SearchParam,
  ServiceErrorCode,
  type SharingSpace,
} from "../types";
import { buildTypePredicateFilter } from "../utils/typeUtils";
import { UserID } from "./useAccount";
import {
  type ProjectData,
  projectQueryKey,
  ProjectViewLinkSchema,
  useProject,
  UserSchema,
} from "./useProject";
import type { ProjectMember } from "./useProjectSharing";
import {
  useViewLinkId,
  useWebExport,
  type WebExportAssignableUserRole,
  type WebExportData,
  webExportQueryKey,
  type WebExportSharedState,
} from "./useWebExport";

const ProjectViewLinksListResponseSchema = z.object({
  projectViewLinks: z.array(ProjectViewLinkSchema),
  users: z.array(UserSchema),
  nextCursor: z.string().optional(),
});

export type ProjectViewLinksListResponseData = z.infer<typeof ProjectViewLinksListResponseSchema>;

export const projectViewLinksQueryKey = ["projectViewLinks"];

export const useProjectViewLinkList = (opts: {
  projectId?: string;
  teamId?: string;
  latestByProject?: boolean;
  viewLinkList?: string[];
  pageSize?: number;
  enabled?: boolean;
}) => {
  const { enabled = true, pageSize = 50 } = opts;
  const query = useInfiniteQuery<ProjectViewLinksListResponseData>({
    queryKey: [
      ...projectViewLinksQueryKey,
      opts.projectId,
      opts.teamId,
      opts.latestByProject,
      opts.viewLinkList,
    ],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      if (opts.projectId) params.set("projectId", opts.projectId);
      if (opts.teamId) params.set("teamId", opts.teamId);
      if (typeof opts.latestByProject === "boolean") {
        params.set("latestByProject", String(opts.latestByProject));
      }
      if (opts.viewLinkList?.length) {
        params.set("includeProjectViewLinkId", opts.viewLinkList.join(","));
      }
      params.set("pageSize", String(pageSize));
      if (pageParam) params.set("cursor", String(pageParam));
      const response = await SyncRequestClient.authenticatedRequest({
        method: "GET",
        url: `/web/project-view-links?${params.toString()}`,
      });
      return ProjectViewLinksListResponseSchema.parse(await response.json());
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: enabled && isLoggedIn() && (Boolean(opts.projectId) || Boolean(opts.teamId)),
  });

  const data = useMemo(() => {
    if (!query.data) return undefined;
    return {
      projectViewLinks: query.data.pages.flatMap((p) => p.projectViewLinks),
      users: query.data.pages.flatMap((p) => p.users),
    };
  }, [query.data]);

  return { ...query, data };
};

export const useProjectViewLinks = () => {
  const { projectId } = useParams();
  const parsedProjectId = ProjectID.safeParse(projectId);
  const visitedViewLinks = useVisitedViewLinks(
    parsedProjectId.success ? parsedProjectId.data : undefined
  );
  const {
    data: viewLinkListData,
    isLoading: isViewLinkListDataLoading,
    isError: isViewLinkListDataError,
  } = useProjectViewLinkList({
    projectId: projectId,
    viewLinkList: visitedViewLinks,
    pageSize: 100,
  });
  const {
    data: projectData,
    isLoading: isProjectDataLoading,
    isError: isProjectDataError,
  } = useProject();
  const { data: webExportData, isLoading: isWebExportDataLoading } = useWebExport();

  return {
    viewLinks: useMemo(() => {
      const viewLinks = projectData?.projectViewLinks ?? viewLinkListData?.projectViewLinks ?? [];
      if (viewLinks.length) {
        return [...viewLinks]
          .sort((a, b) => (a.revisionId < b.revisionId ? 1 : -1))
          .map((viewLink) => ({
            projectViewLinkId: viewLink.projectViewLinkId,
            revisionId: viewLink.revisionId,
            uploadedAt: viewLink.uploadedAt,
            uploadedByUserId: viewLink.uploadedByUserId,
            versionName: viewLink.versionName,
          }));
      }

      if (webExportData?.webExport && webExportData?.viewLinkId) {
        return [
          {
            projectViewLinkId: webExportData.viewLinkId,
            revisionId: webExportData.webExport.revisionId,
            uploadedAt: webExportData.webExport.uploadedAt,
            uploadedByUserId: webExportData.webExport.uploadedByUserId,
            versionName: webExportData.versionName,
          },
        ];
      }

      return [];
    }, [projectData?.projectViewLinks, viewLinkListData?.projectViewLinks, webExportData]),
    users: useMemo(
      () => [...(projectData?.users ?? []), ...(viewLinkListData?.users ?? [])],
      [projectData?.users, viewLinkListData?.users]
    ),
    isLoading:
      isProjectDataLoading ||
      isViewLinkListDataLoading ||
      (isProjectDataError && isViewLinkListDataError && isWebExportDataLoading),
  };
};

type UpdateProjectViewLinkRequestPayload = {
  sharedState?: WebExportSharedState;
  baseRole?: WebExportAssignableUserRole;
  versionName?: string;
  description?: string;
};

export const projectViewLinkSharingQueryKey = ["projectViewLinkSharingQueryKey"];

const projectViewLinkSharingQueriesForViewLink = (viewLinkId?: string) => ({
  queryKey: projectViewLinkSharingQueryKey,
  predicate: (query: { queryKey: QueryKey }) =>
    Boolean(viewLinkId) && query.queryKey.includes(viewLinkId),
});

export const useMutateProjectViewLink = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (variables: UpdateProjectViewLinkRequestPayload) => void;
  onError?: (error: Error, variables: UpdateProjectViewLinkRequestPayload) => void;
  teamID?: string;
} = {}) => {
  const queryClient = useQueryClient();
  const { projectId } = useParams();

  return useMutation({
    mutationFn: async ({
      viewLinkId,
      body,
    }: {
      viewLinkId: string;
      body: UpdateProjectViewLinkRequestPayload;
    }) => {
      await SyncRequestClient.authenticatedRequest({
        method: "PATCH",
        url: `/web/project-view-links/${viewLinkId}`,
        body,
      });
    },
    onSuccess: (_, variables) => {
      const { sharedState, baseRole, versionName, description } = variables.body;

      const updateViewLinksCache = <
        T extends {
          projectViewLinks: {
            projectViewLinkId: string;
            versionName?: string;
            description?: string;
          }[];
        },
      >(
        oldData: T
      ) => {
        const deepCopy = structuredClone(oldData);
        const viewLinks = deepCopy.projectViewLinks;
        if (sharedState === "not_shared") {
          deepCopy.projectViewLinks = viewLinks.filter(
            (item) => item.projectViewLinkId !== variables.viewLinkId
          );
          return deepCopy;
        }

        deepCopy.projectViewLinks = viewLinks.map((item) => {
          if (item.projectViewLinkId !== variables.viewLinkId) {
            return item;
          }

          return {
            ...item,
            versionName: versionName ?? item.versionName,
            description: description ?? item.description,
          };
        });

        return deepCopy;
      };
      queryClient.setQueriesData<ProjectData | undefined>(
        { queryKey: [...projectQueryKey, projectId] },
        (oldData) => {
          if (!oldData) {
            return oldData;
          }

          const deepCopy = structuredClone(oldData);
          const viewLinks = deepCopy.projectViewLinks;
          if (!viewLinks.length) {
            return deepCopy;
          }

          if (sharedState === "not_shared") {
            deepCopy.projectViewLinks = viewLinks.filter(
              (item) => item.projectViewLinkId !== variables.viewLinkId
            );
            return deepCopy;
          }

          deepCopy.projectViewLinks = viewLinks.map((item) => {
            if (item.projectViewLinkId !== variables.viewLinkId) {
              return item;
            }

            return {
              ...item,
              versionName: versionName ?? item.versionName,
              description: description ?? item.description,
            };
          });

          return deepCopy;
        }
      );

      queryClient.setQueriesData<WebExportData | undefined>(
        { queryKey: [...webExportQueryKey, variables.viewLinkId] },
        (oldData) => {
          if (!oldData) {
            return oldData;
          }

          const deepCopy = structuredClone(oldData);
          return {
            ...deepCopy,
            sharedState: sharedState ?? deepCopy.sharedState,
            baseRole: baseRole ?? deepCopy.baseRole,
            versionName: versionName ?? deepCopy.versionName,
            description: description ?? deepCopy.description,
          };
        }
      );

      queryClient.setQueriesData<ProjectViewLinkSharingResponseData | undefined>(
        projectViewLinkSharingQueriesForViewLink(variables.viewLinkId),
        (oldData) => {
          if (!oldData) {
            return oldData;
          }

          const deepCopy = structuredClone(oldData);
          return {
            ...deepCopy,
            sharedState: sharedState ?? deepCopy.sharedState,
            baseRole: baseRole ?? deepCopy.baseRole,
          };
        }
      );
      queryClient.setQueriesData<InfiniteData<ProjectViewLinksListResponseData> | undefined>(
        { queryKey: [...projectViewLinksQueryKey] },
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            pages: oldData.pages.map(updateViewLinksCache),
          };
        }
      );
      onSuccess?.(variables.body);
    },
    onError: (error, variables) => {
      LogSentryException({ messagePrefix: "Failed to update project viewlink", error });
      onError?.(error, variables.body);
    },
  });
};

type ProjectViewLinkUserBase = {
  email: string;
  role: WebExportAssignableUserRole;
};

export type ProjectViewLinkMember = ProjectViewLinkUserBase & {
  userId: UserID;
  name?: string;
};

export const ProjectViewLinkInvitationID = z.string().brand("ProjectViewLinkInvitationID");
export type ProjectViewLinkInvitationID = z.infer<typeof ProjectViewLinkInvitationID>;

export type ProjectViewLinkInvitation = ProjectViewLinkUserBase & {
  invitationId: ProjectViewLinkInvitationID;
};

export type ProjectViewLinkUser = ProjectViewLinkMember | ProjectViewLinkInvitation;

export type ProjectViewLinkSharingResponseData = {
  users: ProjectViewLinkUser[];
  baseRole: WebExportAssignableUserRole;
  sharedState: WebExportSharedState;
  space?: SharingSpace;
  projectUsers: ProjectMember[];
};

export const isProjectViewLinkInvitation = buildTypePredicateFilter<
  ProjectViewLinkUser,
  ProjectViewLinkInvitation
>("invitationId");

export const isProjectViewLinkMember = buildTypePredicateFilter<
  ProjectViewLinkUser,
  ProjectViewLinkMember
>("userId");

export const useProjectViewLinkSharing = () => {
  const viewLinkId = useViewLinkId();
  const { projectId } = useParams();
  const { data: WebExportData } = useWebExport();

  return useQuery({
    queryKey: [...projectViewLinkSharingQueryKey, projectId, viewLinkId],
    queryFn: async () => {
      const response = await SyncRequestClient.authenticatedRequest({
        method: "GET",
        url: `/web/project-view-links/${viewLinkId}/sharing`,
      });

      return response.json<ProjectViewLinkSharingResponseData>();
    },
    select: (data) => {
      const members: ProjectViewLinkMember[] = data.users.filter(isProjectViewLinkMember);
      const invitations: ProjectViewLinkInvitation[] = data.users.filter(
        isProjectViewLinkInvitation
      );

      return { ...data, members, invitations };
    },
    staleTime: 20 * 60 * 1000,
    enabled:
      Boolean(viewLinkId) &&
      isLoggedIn() &&
      WebExportData?.userRole &&
      ProjectRoleUtils.isAtLeast(WebExportData?.userRole, "editor"),
  });
};

export type ProjectViewLinkGetInvitationResponseData = {
  email?: string;
  action: "view" | "accept" | "signup" | "login";
};

export const projectViewLinkInvitationQueryKey = ["projectViewLinkInvitationQueryKey"];

export const useProjectViewLinkInvitation = () => {
  const viewLinkId = useViewLinkId();
  const [searchParams] = useSearchParams();
  const invitationHash = searchParams.get(SearchParam.InvitationHash) ?? "";

  const enabled = Boolean(invitationHash);

  const query = useQuery({
    queryKey: [...projectViewLinkInvitationQueryKey, viewLinkId, invitationHash],
    queryFn: async () => {
      const request = isLoggedIn()
        ? SyncRequestClient.authenticatedRequest
        : SyncRequestClient.publicRequest;

      const response = await request({
        method: "GET",
        url: `/web/project-view-links/${viewLinkId}/invitations-by-hash/${invitationHash}`,
      });

      return response.json<ProjectViewLinkGetInvitationResponseData>();
    },
    enabled,
  });

  return { ...query, enabled };
};

export type CreateProjectViewLinkInvitationRequestPayload = {
  emails: string[];
  role: WebExportAssignableUserRole;
};

export type CreateProjectViewLinkInvitationResponseData = {
  invitedUsers: ProjectViewLinkInvitation[];
  errors: {
    email: string;
    message: string;
    code: string;
  }[];
};

export const useMutateCreateProjectViewLinkInvitation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (
    data: CreateProjectViewLinkInvitationResponseData,
    variables: CreateProjectViewLinkInvitationRequestPayload
  ) => void;
  onError?: (error: Error, variables: CreateProjectViewLinkInvitationRequestPayload) => void;
} = {}) => {
  const viewLinkId = useViewLinkId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateProjectViewLinkInvitationRequestPayload) => {
      const response = await SyncRequestClient.authenticatedRequest({
        method: "POST",
        url: `/web/project-view-links/${viewLinkId}/invitations`,
        body,
      });

      return response.json<CreateProjectViewLinkInvitationResponseData>();
    },
    onSuccess: (data, variables) => {
      queryClient.setQueriesData<ProjectViewLinkSharingResponseData | undefined>(
        projectViewLinkSharingQueriesForViewLink(viewLinkId),
        (oldData) => {
          if (!oldData) {
            return oldData;
          }

          const deepCopy = JSON.parse(JSON.stringify(oldData)) as typeof oldData;
          const invitations = oldData.users.filter(isProjectViewLinkInvitation);
          const members = oldData.users.filter(isProjectViewLinkMember);

          const mappedUsers = [...members, ...data.invitedUsers, ...invitations];

          return {
            ...deepCopy,
            users: mappedUsers,
          };
        }
      );

      onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      LogSentryException({ messagePrefix: "Failed to create invitations", error });
      onError?.(error, variables);
    },
  });
};

export const useMutateAcceptInvitation = () => {
  const viewLinkId = useViewLinkId();
  const [searchParams] = useSearchParams();
  const invitationHash = searchParams.get(SearchParam.InvitationHash) ?? "";

  return useMutation({
    mutationFn: async ({ emailToken }: { emailToken?: string | null } = {}) => {
      try {
        return await SyncRequestClient.authenticatedRequest({
          method: "POST",
          url: `/web/project-view-links/${viewLinkId}/invitations-by-hash/${invitationHash}/accept`,
          body: { ...(emailToken && { emailToken }) },
        });
      } catch (error) {
        // Accept invitation is best effort.
        // Not found could mean you can view the viewlink or could mean you can't.
        // Up to the viewlink loading to decide and show the proper error screen.
        if (
          error instanceof ServiceError &&
          (error.hasCode(ServiceErrorCode.NotFound) ||
            error.hasCode(ServiceErrorCode.InvitationNotFound))
        ) {
          return;
        }
        throw error;
      }
    },
  });
};

export type UpdateProjectViewLinkUserRequestPayload = {
  role: WebExportAssignableUserRole;
};

export const useMutateUpdateProjectViewLinkUser = ({
  userId,
  onSuccess,
  onError,
}: {
  userId: UserID;
  onSuccess?: (variables: UpdateProjectViewLinkUserRequestPayload) => void;
  onError?: (error: Error, variables: UpdateProjectViewLinkUserRequestPayload) => void;
}) => {
  const viewLinkId = useViewLinkId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: UpdateProjectViewLinkUserRequestPayload) => {
      const response = await SyncRequestClient.authenticatedRequest({
        method: "PUT",
        url: `/web/project-view-links/${viewLinkId}/users/${userId}`,
        body,
      });

      return response.json<ProjectViewLinkMember>();
    },
    onSuccess: (data, variables) => {
      queryClient.setQueriesData<ProjectViewLinkSharingResponseData | undefined>(
        projectViewLinkSharingQueriesForViewLink(viewLinkId),
        (oldData) => {
          if (!oldData) {
            return oldData;
          }

          const deepCopy = JSON.parse(JSON.stringify(oldData)) as typeof oldData;
          const mappedUsers = deepCopy.users.map((user) => {
            if (isProjectViewLinkMember(user) && user.userId === userId) {
              return {
                ...data,
              };
            }

            return user;
          });

          return {
            ...deepCopy,
            users: mappedUsers,
          };
        }
      );

      onSuccess?.(variables);
    },
    onError: (error, variables) => {
      LogSentryException({ messagePrefix: "Failed to update project viewlink user", error });
      onError?.(error, variables);
    },
  });
};

export type UpdateProjectViewLinkInvitationRequestPayload = {
  role: WebExportAssignableUserRole;
};

export const useMutateUpdateProjectViewLinkInvitation = ({
  invitationId,
  onSuccess,
  onError,
}: {
  invitationId: ProjectViewLinkInvitationID;
  onSuccess?: (variables: UpdateProjectViewLinkInvitationRequestPayload) => void;
  onError?: (error: Error, variables: UpdateProjectViewLinkInvitationRequestPayload) => void;
}) => {
  const viewLinkId = useViewLinkId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: UpdateProjectViewLinkInvitationRequestPayload) => {
      const response = await SyncRequestClient.authenticatedRequest({
        method: "PUT",
        url: `/web/project-view-links/${viewLinkId}/invitations/${invitationId}`,
        body,
      });

      return response.json<ProjectViewLinkInvitation>();
    },
    onSuccess: (data, variables) => {
      queryClient.setQueriesData<ProjectViewLinkSharingResponseData | undefined>(
        projectViewLinkSharingQueriesForViewLink(viewLinkId),
        (oldData) => {
          if (!oldData) {
            return oldData;
          }

          const deepCopy = JSON.parse(JSON.stringify(oldData)) as typeof oldData;
          const mappedUsers = deepCopy.users.map((user) => {
            if (isProjectViewLinkInvitation(user) && user.invitationId === invitationId) {
              return {
                ...data,
              };
            }

            return user;
          });

          return {
            ...deepCopy,
            users: mappedUsers,
          };
        }
      );

      onSuccess?.(variables);
    },
    onError: (error, variables) => {
      LogSentryException({ messagePrefix: "Failed to update project viewlink invitation", error });
      onError?.(error, variables);
    },
  });
};

export const useMutateDeleteProjectViewLinkUser = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: UserID, variables: { userId: UserID }) => void;
  onError?: (error: Error, variables: { userId: UserID }) => void;
} = {}) => {
  const viewLinkId = useViewLinkId();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ userId }: { userId: UserID }) => {
      await SyncRequestClient.authenticatedRequest({
        method: "DELETE",
        url: `/web/project-view-links/${viewLinkId}/users/${userId}`,
      });
      return userId;
    },
    onSuccess: (data, variables) => {
      onSuccess?.(data, variables);
      removeUserFromCache(data);
    },
    onError: (error, variables) => {
      LogSentryException({ messagePrefix: "Failed to delete project viewlink user", error });
      onError?.(error, variables);
    },
  });

  const removeUserFromCache = (userId: UserID) => {
    queryClient.setQueriesData<ProjectViewLinkSharingResponseData | undefined>(
      projectViewLinkSharingQueriesForViewLink(viewLinkId),
      (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          users: oldData.users.filter(
            (user) => !(isProjectViewLinkMember(user) && user.userId === userId)
          ),
        };
      }
    );
  };

  return {
    ...mutation,
    removeUserFromCache,
  };
};

export const useMutateDeleteProjectViewLinkInvitation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (
    data: ProjectViewLinkInvitationID,
    variables: { invitationId: ProjectViewLinkInvitationID }
  ) => void;
  onError?: (error: Error, variables: { invitationId: ProjectViewLinkInvitationID }) => void;
} = {}) => {
  const viewLinkId = useViewLinkId();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ invitationId }: { invitationId: ProjectViewLinkInvitationID }) => {
      await SyncRequestClient.authenticatedRequest({
        method: "DELETE",
        url: `/web/project-view-links/${viewLinkId}/invitations/${invitationId}`,
      });
      return invitationId;
    },
    onSuccess: (data, variables) => {
      onSuccess?.(data, variables);
      removeInvitationFromCache(data);
    },
    onError: (error, variables) => {
      LogSentryException({ messagePrefix: "Failed to delete project viewlink invitation", error });
      onError?.(error, variables);
    },
  });

  const removeInvitationFromCache = (invitationId: ProjectViewLinkInvitationID) => {
    queryClient.setQueriesData<ProjectViewLinkSharingResponseData | undefined>(
      projectViewLinkSharingQueriesForViewLink(viewLinkId),
      (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          users: oldData.users.filter(
            (user) => !(isProjectViewLinkInvitation(user) && user.invitationId === invitationId)
          ),
        };
      }
    );
  };

  return {
    ...mutation,
    removeInvitationFromCache,
  };
};
